import { useState } from 'react';
import { LuBuilding2 } from 'react-icons/lu';
import Drawer from '../Drawer/Drawer';
import { createBid, fetchMyPartner, notifyAdmins } from '../../lib/directus';
import styles from './LoadBidDrawer.module.css';

function Stars({ rating }) {
  return (
    <span className={styles.stars}>
      {[1, 2, 3, 4, 5].map(i => (
        <span key={i} style={{ color: i <= Math.round(rating) ? '#f59e0b' : '#d1d5db' }}>★</span>
      ))}
      <span className={styles.ratingText}>{rating.toFixed(1)}</span>
    </span>
  );
}

export default function LoadBidDrawer({ load, onClose, onSubmit }) {
  const [bidAmount, setBidAmount] = useState('');
  const [loading, setLoading]    = useState(false);
  const [error, setError]        = useState('');

  if (!load) return null;

  async function handleSubmit() {
    if (!bidAmount) return;
    setLoading(true);
    setError('');
    try {
      const partner = await fetchMyPartner();
      await createBid({
        load:        load._id,
        bid_amount:  parseFloat(bidAmount.replace(/[^0-9.]/g, '')) || bidAmount,
        transporter: partner?.id,
      });
      notifyAdmins(
        'New bid submitted',
        `A new bid of R${parseFloat(bidAmount).toLocaleString()} has been submitted for load ${load.loadId} (${load.pickup} → ${load.delivery}). Log in to review bids.`
      ).catch(console.error);
      setBidAmount('');
      onSubmit && onSubmit();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const footer = (
    <button
      className={styles.submitBtn}
      onClick={handleSubmit}
      disabled={!bidAmount || loading}
    >
      {loading ? 'Submitting...' : 'Submit bid'}
    </button>
  );

  return (
    <Drawer isOpen={!!load} title={load.loadId} onClose={onClose} footer={footer}>
      {/* Seller card */}
      <div className={styles.card}>
        <div className={styles.sellerRow}>
          <div className={styles.sellerIcon}><LuBuilding2 size={18} color="#9ca3af" /></div>
          <div>
            <p className={styles.sellerName}>{load.seller}</p>
            <div className={styles.sellerMeta}>
              <Stars rating={load.sellerRating} />
              <span className={styles.tradesText}>{load.sellerTrades} trades</span>
            </div>
          </div>
        </div>
      </div>

      {/* Route card */}
      <div className={styles.card}>
        <p className={styles.cardLabel}>Route</p>
        <div className={styles.routeRow}>
          <div className={styles.pickupBlock}>
            <span className={styles.routeLabel}>PICKUP</span>
            <span className={styles.routeName}>{load.pickup}</span>
          </div>
          <span className={styles.routeArrow}>→</span>
          <div className={styles.deliveryBlock}>
            <span className={styles.routeLabel}>DELIVERY</span>
            <span className={styles.routeName}>{load.delivery}</span>
          </div>
        </div>
        <div className={styles.cardDivider} />
        <div className={styles.distanceRow}>
          <span className={styles.distanceLabel}>Distance &amp; Est time</span>
          <span className={styles.distanceValue}>{load.distance} • {load.estimatedTime}</span>
        </div>
      </div>

      {/* Cargo details */}
      <div className={styles.card}>
        <p className={styles.cardTitle}>Cargo details</p>
        <div className={styles.threeCol}>
          <div>
            <p className={styles.fieldLabel}>Commodity</p>
            <p className={styles.fieldValue}>{load.commodity || '-'}</p>
          </div>
          <div>
            <p className={styles.fieldLabel}>Grade</p>
            <p className={styles.fieldValue}>{load.grade || '-'}</p>
          </div>
          <div>
            <p className={styles.fieldLabel}>Quantity</p>
            <p className={styles.fieldValue}>{load.quantity}</p>
          </div>
        </div>
      </div>

      {/* Schedule */}
      <div className={styles.card}>
        <p className={styles.cardTitle}>Schedule</p>
        <div className={styles.threeCol}>
          <div>
            <p className={styles.fieldLabel}>Pickup date</p>
            <p className={styles.fieldValue}>{load.pickupDate}</p>
          </div>
          <div>
            <p className={styles.fieldLabel}>Pickup window</p>
            <p className={styles.fieldValue}>{load.pickupWindow}</p>
          </div>
          <div>
            <p className={styles.fieldLabel}>Delivery deadline</p>
            <p className={styles.fieldValue}>{load.deliveryDeadline}</p>
          </div>
        </div>
      </div>

      {/* Bidding info */}
      <div className={styles.card}>
        <p className={styles.cardTitle}>Bidding info</p>
        <div className={styles.twoCol}>
          <div>
            <p className={styles.fieldLabel}>Bids submitted</p>
            <p className={styles.fieldValue}>{load.bidsSubmitted}</p>
          </div>
          <div>
            <p className={styles.fieldLabel}>Bid closes</p>
            <p className={`${styles.fieldValue} ${styles.redText}`}>{load.bidCloses}</p>
          </div>
        </div>
      </div>

      {/* Your bid */}
      <div className={styles.card}>
        <p className={styles.cardTitle}>Your bid</p>
        <div className={styles.bidRow}>
          <span className={styles.bidPrefix}>USD</span>
          <input
            className={styles.bidInput}
            value={bidAmount}
            onChange={e => setBidAmount(e.target.value)}
            placeholder="Enter your bid amount"
            type="number"
            min="0"
          />
        </div>
      </div>

      {error && (
        <p style={{ color: 'var(--red-color)', fontSize: 12, fontFamily: '"General Sans", sans-serif', padding: '0 4px' }}>
          {error}
        </p>
      )}
    </Drawer>
  );
}
